import PropTypes from "prop-types"
import React, { useState } from "react"
import { useMutation } from "react-query"
import TipPresetsEditor from "src/main/Settings/Hardware/TipPresetsEditor"

import Toggle from "src/components/Toggle"

import { toggleOnReaderTipSetting } from "src/api/Settings"

import { useToast } from "src/hooks/use_toast"

const TipSettings = ({
  onReaderTippingEnabled,
  tipPresetsEligible,
  tipPresets,
}) => {
  const [checked, setChecked] = useState(onReaderTippingEnabled)
  // Tracks the server-confirmed tipping state. The presets editor gates on this
  // rather than the optimistic `checked` so it can't be used to save presets
  // while an enable request is still in flight or after one has failed.
  const [tippingConfirmed, setTippingConfirmed] = useState(
    onReaderTippingEnabled
  )
  const [presets, setPresets] = useState(tipPresets)
  const showToast = useToast()

  const { mutate, isLoading } = useMutation(
    (data) => toggleOnReaderTipSetting({ data }),
    {
      onSuccess: (data) => {
        setChecked(data.onReaderTippingEnabled)
        setTippingConfirmed(data.onReaderTippingEnabled)
        const message = data.onReaderTippingEnabled
          ? "Tipping enabled"
          : "Tipping disabled"
        showToast(message, { type: "success" })
      },
      onError: () => {
        setChecked((current) => !current)
        showToast(
          "Something went wrong. Please reload the page and contact mayday@dockwa.com if the issue persists.",
          { type: "error" }
        )
      },
    }
  )

  const handleClick = () => {
    const next = !checked
    setChecked(next)
    mutate({ allow_on_reader_tips: next })
  }

  return (
    <>
      <h2 className="mb-4 mt-0 text-lg font-semibold">Tip Settings</h2>
      <div className="flex">
        <Toggle
          checked={checked}
          onChange={handleClick}
          name="allowOnReaderTips"
          disabled={isLoading}
        />
        <div className="ml-2 font-normal text-gray-700">
          Allow credit card tips
        </div>
      </div>
      <p className="mt-2 text-sm text-gray-500">
        Changes to your tip settings can take a few minutes to appear on your
        reader.
      </p>
      {tippingConfirmed && tipPresetsEligible && (
        <TipPresetsEditor tipPresets={presets} onSaved={setPresets} />
      )}
    </>
  )
}

TipSettings.propTypes = {
  onReaderTippingEnabled: PropTypes.bool.isRequired,
  tipPresetsEligible: PropTypes.bool,
  tipPresets: PropTypes.shape({
    mode: PropTypes.oneOf(["fixed", "percentage", "smart"]).isRequired,
    fixed_amounts: PropTypes.arrayOf(PropTypes.number),
    percentages: PropTypes.arrayOf(PropTypes.number),
    smart_tip_threshold: PropTypes.number,
  }),
}

TipSettings.defaultProps = {
  tipPresetsEligible: false,
  tipPresets: { mode: "fixed", fixed_amounts: [500, 1000, 1500] },
}

export default TipSettings
